import React, {useEffect, useState, useRef} from "react";
import {useGlobalContext} from "../context/GlobalState";
import {convertMicrView, convertMicrEdit, ReadableLocaleDate, ToCurrency} from "../services/Utils";
import {getCheckImages, getCustomerConfig, getReviews} from "../services/Request";
import {UpdateCheckAmount, UpdateCheckMICR, UpdateReviewState,} from "../services/Update";
import TrueChecksActionCell from "./TrueChecksActionCell";
import RejectCheckDialog from "./RejectCheckDialog";
import DepositImgDialog from "./DepositImgDialog";

// Material UI Imports
import {
    Box,
    Button,
    FormControl,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from '@mui/material/LinearProgress';

// Icons
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import Warning from "@mui/icons-material/Warning";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ClearIcon from '@mui/icons-material/Clear';

// Other Imports
import {enqueueSnackbar, closeSnackbar} from "notistack";
import {Snackbar} from "./Snackbar";

const styles = {
    tableHeader: {
        fontWeight: "bold",
        textAlign: "center",
        textWrap: "nowrap",
        width: "12.5%",
        p: 1,
    },
    tableCell: {
        textAlign: "center",
        textWrap: "nowrap",
        width: "12.5%",
        height: "50px",
        px: 2,
        py: .5
    },
};


export function SelectedCheck({selectedCheck, setSelectedCheck, sortedCheckIds, connectionId}) {
    const theme = useTheme();
    const [checkImages, setCheckImages] = useState({
        frontImage: null,
        rearImage: null,
    });
    const [openImgModal, setOpenImgModal] = useState(false);
    const [checkImagesData, setCheckImagesData] = useState([]);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [checkSide, setCheckSide] = useState("frontImage");
    const [loading, setLoading] = useState({isLoading: false, status: null});
    const [allowEditAmount, setAllowEditAmount] = useState(false);
    const [allowEditMicr, setAllowEditMicr] = useState(false);
    const [editedAmount, setEditedAmount] = useState(selectedCheck?.itemAmount);
    const [editedMicr, setEditedMicr] = useState(selectedCheck?.micr);
    const [currentCheckIndex, setCurrentCheckIndex] = useState(0);
    const [filteredChecks, setFilteredChecks] = useState([]);
    const {getCheckData, user, checkData, customerConfigs, startDate, endDate} = useGlobalContext();
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const statusUpdateQueueRef = useRef([]);

    // Filter checks based on tab status and set selected check
    useEffect(() => {
        const filteredChecks = sortedCheckIds.map(id => checkData.find(check => check.checkID === id));
        setFilteredChecks(filteredChecks);
        const selected = filteredChecks.find(check => check?.checkID === selectedCheck?.checkID) || filteredChecks[0] || null;
        fetchCheckImages(selected);
        setSelectedCheck(selected);
        setEditedAmount(Number(selected?.itemAmount).toFixed(2));
        setEditedMicr(selected?.micr);
        setAllowEditAmount(false);
        setAllowEditMicr(false);
        setCurrentCheckIndex(filteredChecks.indexOf(selected) + 1);
    }, [selectedCheck, sortedCheckIds, checkData]);
    
    /**
     * Fetches and sets the images for the given check.
     * If the check is null, it resets the check images to null.
     * It also preloads the images for the previous and next checks in the filtered list.
     *
     * @param {Object} check - The check object containing tranID and itemID.
     */
    async function fetchCheckImages(check) {
        if (!check) {
            setCheckImages({frontImage: null, rearImage: null});
            return;
        }

        setIsLoadingImage(true);

        const fetchAndSetImages = async (check) => {
            const existingImages = checkImagesData.find(data => data.checkID === check.checkID);
            if (existingImages) {
                return existingImages;
            }

            const images = await getCheckImages(check.tranID, check.itemID);
            setCheckImagesData(prevState =>
                prevState.some(data => data.checkID === check.checkID)
                    ? prevState
                    : [...prevState, { checkID: check.checkID, ...images }]
            );
            return images;
        };

        const images = await fetchAndSetImages(check);
        setCheckImages(images);
        setIsLoadingImage(false);

        const previousCheck = filteredChecks[filteredChecks.indexOf(check) - 1];
        const nextCheck = filteredChecks[filteredChecks.indexOf(check) + 1];

        if (previousCheck) {
            fetchAndSetImages(previousCheck);
        }
        if (nextCheck) {
            fetchAndSetImages(nextCheck);
        }
    }

    const handleCheckDirection = (direction) => {
        const currentIndex = filteredChecks.findIndex((check) => check.checkID === selectedCheck?.checkID);
        const nextCheckIndex = currentIndex + direction;

        if (nextCheckIndex >= 0 && nextCheckIndex < filteredChecks.length) {
            setSelectedCheck(filteredChecks[nextCheckIndex]);
        }
    };

    const handleAmountChange = (event) => {
        const {value} = event.target;
        const regex = /^[0-9]*(\.[0-9]{0,2})?$/;
        if (regex.test(value) && value <= 1000000) {
            setEditedAmount(value);
        }
    };

    const handleMicrChange = (event) => {
        const {value} = event.target;
        const regex = /^[0-9":<\-= ]*$/;
        if (value.length <= 62 && regex.test(value)) {
            setEditedMicr(value);
        }
    }

    async function handleShowEditAmount() {
        try {
            if (allowEditAmount && editedAmount !== selectedCheck?.itemAmount) {
                setLoading({isLoading: true, status: 'amount'});
                const response = await UpdateCheckAmount(
                    selectedCheck.tranID,
                    selectedCheck.itemID,
                    editedAmount
                );

                if (response.status === 200) {
                    Snackbar({message: "Successfully updated check amount.", variant: 'success'});
                    if(response?.data?.variant){
                        Snackbar({message: response.data.message, variant: response.data.variant});
                    }
                    selectedCheck.itemAmount = editedAmount;
                    setLoading({isLoading: false, status: null});
                    getCheckData();
                }
            }
            setAllowEditAmount((show) => !show);
        } catch (error) {
            Snackbar({message: "Unable to update check amount.", variant: 'error'});
            setLoading({isLoading: false, status: null});
            console.error(error);
        }
    }

    async function handleShowEditMicr() {
        try {
            if (allowEditMicr && editedMicr !== selectedCheck?.micr) {
                setLoading({isLoading: true, status: 'micr'});
                const response = await UpdateCheckMICR(
                    selectedCheck.tranID,
                    selectedCheck.itemID,
                    editedMicr
                );

                if (response.status === 200) {
                    Snackbar({message: "Successfully updated check amount.", variant: 'success'});
                    if(response?.data?.variant){
                        Snackbar({message: response.data.message, variant: response.data.variant});
                    }
                    selectedCheck.micr = editedMicr;
                    setLoading({isLoading: false, status: null});
                    getCheckData();
                }
            }
            setAllowEditMicr((show) => !show);
        } catch (error) {
            Snackbar({message: "Unable to update check MICR", variant: 'error'});
            setLoading({isLoading: false, status: null});
            console.error(error);
        }
    }

    async function handleStatusUpdate(status, rejectReason = null) {
        setLoading({isLoading: true, status});
        const currentCheck = selectedCheck;
        try {
            const checkInfo = await getReviews("all", startDate, endDate)
                .then(result => result.find(reviewed =>
                    reviewed.tranID === currentCheck?.tranID &&
                    reviewed.itemID === currentCheck?.itemID
                ));
            if (checkInfo.reviewState === status) {
                getCheckData();
                return setLoading({isLoading: false, status: null});
            }
            
            enqueueSnackbar(`Submitting ${status.toLowerCase() === 'approved' ? 'approval' : 'rejection'} for deposit ${currentCheck?.checkID} for $${currentCheck?.itemAmount}`, {
                key: currentCheck.checkID,
                variant: 'info',
                action: <UndoBtn currentCheck={currentCheck} />,
            });

            statusUpdateQueueRef.current.push(currentCheck?.checkID);
            setLoading({isLoading: false, status: null});
            handleCheckDirection(1);

            setTimeout(async () => {
                if (statusUpdateQueueRef.current.includes(currentCheck?.checkID)) {
                    let response;
                    try{
                        response = await UpdateReviewState(
                            currentCheck.tranID,
                            currentCheck.itemID,
                            status,
                            user.username,
                            rejectReason
                        ); 
                    } catch {
                        response = null;
                    }
                    
                    if (response !== null && response?.status === 200) {
                        Snackbar({message: 'Status change successful', variant: 'success'});
                        getCheckData();
                    } else {
                        Snackbar({message: `Unable to update status for ${currentCheck.checkID}`, variant: 'error'});
                    }

                    statusUpdateQueueRef.current = statusUpdateQueueRef.current.filter(id => id !== currentCheck.checkID);
                }
            }, 10000);

        } catch (error) {
            Snackbar({message: "Unable to update check status", variant: 'error'});
            setLoading({isLoading: false, status: null});
            console.error(error);
        }
    }

    function undoStatusUpdate(currentCheck) {
        try {
            //Remove check from status queue
            statusUpdateQueueRef.current = statusUpdateQueueRef.current.filter(id => id !== currentCheck.checkID);
            //If check is selected check, reset selected check to current check to trigger re-render
            setSelectedCheck(selectedCheck === currentCheck ? {...currentCheck} : currentCheck);
        } catch (error) {
            Snackbar({message: "Unable to undo check status.", variant: 'error'});
            console.error(error);
        }
    }

    const UndoBtn = ({currentCheck}) => {
        const [timeLeft, setTimeLeft] = useState(10);
        const [percentage, setPercentage] = useState(100);

        useEffect(() => {
            if (timeLeft > 0) {
                const interval = setInterval(() => {
                    setTimeLeft(seconds => seconds - 1);
                    setPercentage(percent => percent - 10);
                }, 1000);
                return () => clearInterval(interval);
            }
        }, [timeLeft]);

        return (
            <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                    undoStatusUpdate(currentCheck);
                    closeSnackbar(currentCheck.checkID);
                }}
                sx={{pt: 0, pb: .5, m: 0}}
            >
                <Box display="flex" flexDirection="column">
                    UNDO
                    <LinearProgress color="inherit" variant="determinate" value={percentage}/>
                </Box>
            </Button>
        );
    };

    function handleCheckSide() {
        setCheckSide((prevCheckSide) =>
            prevCheckSide === "frontImage" ? "rearImage" : "frontImage"
        );
    }

    function handleKeyDownAmount(event) {
        if (event.key === 'Enter') {
            handleShowEditAmount();
        } else if (event.key === 'Escape') {
            cancelAmountUpdate();
        }
    }

    function handleKeyDownMicr(event) {
        if (event.key === 'Enter') {
            handleShowEditMicr();
        } else if (event.key === 'Escape') {
            cancelMicrUpdate();
        }
    }

    function cancelAmountUpdate() {
        setAllowEditAmount(false);
        setEditedAmount(selectedCheck.itemAmount);
    }

    function cancelMicrUpdate() {
        setAllowEditMicr(false);
        setEditedMicr(selectedCheck.micr)
    }

    function showAmountField() {
        return (
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textWrap: "nowrap",
            }}>
                {allowEditAmount
                    ? showEditAmount()
                    : ToCurrency(selectedCheck.itemAmount)}

                {!user.isViewOnlyRole && selectedCheck?.reviewState === "Pending" && !selectedCheck?.locked && !statusUpdateQueueRef.current.includes(selectedCheck?.checkID) && connectionId && (
                    <>
                        {loading?.isLoading && loading?.status === "amount" ? (
                            <CircularProgress size={20} sx={{ml: 1}}/>
                        ) : (
                            <>
                                <IconButton
                                    aria-label={allowEditAmount ? "Confirm Amount" : "Edit Amount"}
                                    onClick={handleShowEditAmount}
                                    sx={{
                                        m: 0,
                                        px: .5,
                                        py: 0,
                                        borderRadius: 0
                                    }}>
                                    {allowEditAmount ? <CheckIcon titleAccess="Confirm Amount"/> :
                                        <EditIcon titleAccess="Edit Amount"/>}
                                </IconButton>
                                {allowEditAmount && (
                                    <IconButton
                                        sx={{
                                            m: 0,
                                            px: .5,
                                            py: 0,
                                            borderRadius: 0
                                        }}
                                        onClick={() => {
                                            cancelAmountUpdate();
                                        }}
                                    >
                                        <ClearIcon titleAccess="Cancel"/>
                                    </IconButton>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        );
    }

    function showMicrField() {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textWrap: "nowrap",
                }}
            >
                {selectedCheck.validMicr === false && (
                    <Tooltip title={"This MICR appears to be in an invalid format."}>
                        <Warning sx={{
                            color: theme.palette.accent.error,
                            marginRight: .5,
                        }}/>
                    </Tooltip>
                )}
                {allowEditMicr ? showEditMicr() : convertMicrView(selectedCheck.micr)}
                {!user.isViewOnlyRole && selectedCheck?.reviewState === "Pending" && !selectedCheck?.locked && !statusUpdateQueueRef.current.includes(selectedCheck?.checkID) && connectionId && (
                    <>
                        {loading?.isLoading && loading?.status === "micr" ? (
                            <CircularProgress size={20} sx={{ml: 1}}/>
                        ) : (
                            <>
                                <IconButton
                                    aria-label={allowEditMicr ? "Confirm MICR" : "Edit MICR"}
                                    onClick={handleShowEditMicr}
                                    sx={{
                                        m: 0,
                                        px: .5,
                                        py: 0,
                                        borderRadius: 0
                                    }}
                                >
                                    {allowEditMicr ? <CheckIcon titleAccess="Confirm MICR"/> :
                                        <EditIcon titleAccess="Edit MICR"/>}
                                </IconButton>
                                {allowEditMicr && (
                                    <IconButton
                                        sx={{
                                            m: 0,
                                            px: .5,
                                            py: 0,
                                            borderRadius: 0
                                        }}
                                        onClick={() => {
                                            cancelMicrUpdate();
                                        }}
                                    >
                                        <ClearIcon titleAccess="Cancel"/>
                                    </IconButton>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        );
    }

    function showEditAmount() {

        return (
            <FormControl sx={{m: 0, width: "100px"}} variant="standard">
                <TextField
                    type="text"
                    label="New Amount"
                    key={selectedCheck?.itemAmount}
                    value={editedAmount}
                    onChange={handleAmountChange}
                    onKeyDown={handleKeyDownAmount}
                    disabled={!allowEditAmount
                        || selectedCheck?.locked
                        || user.isViewOnlyRole
                        || statusUpdateQueueRef.current.includes(selectedCheck?.checkID)
                        || !connectionId
                    }
                    inputProps={{
                        step: "any" //Disables the tooltip "Please enter a valid value. The two nearest valid values are x and y."
                    }}
                    sx={{
                        input: {
                            fontSize: "fontSize",
                            m: 0,
                            p: .5,
                            textAlign: "center",
                        },
                    }}
                />
            </FormControl>
        );
    }

    function showEditMicr() {
        return (
            <FormControl sx={{m: 0, width: "260px"}} variant="standard">
                <TextField
                    type="text"
                    label="New MICR"
                    key={selectedCheck?.micr}
                    value={editedMicr}
                    defaultValue={convertMicrEdit(selectedCheck?.micr)}
                    onChange={handleMicrChange}
                    onKeyDown={handleKeyDownMicr}
                    disabled={
                        !allowEditMicr
                        || selectedCheck?.locked
                        || user.isViewOnlyRole
                        || statusUpdateQueueRef.current.includes(selectedCheck?.checkID)
                        || !connectionId
                    }
                    sx={{
                        input: {
                            fontSize: "fontSize",
                            m: 0,
                            p: .5,
                            textAlign: "center",
                            width: "260px"
                        },
                    }}
                />
            </FormControl>
        );
    }

    function handleOpenRejectDialog() {
        setOpenRejectDialog(true);
    }

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    backgroundColor: "transparent",
                    width: "100%",
                    height: "100%",
                    padding: 0,
                    rowGap: 2,
                }}
            >
                {/* Check image */}
                <Box
                    sx={{
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "300px",
                        maxWidth: "800px",
                        padding: 0,
                    }}
                >
                    {isLoadingImage ? (
                        <CircularProgress sx={{color: theme.palette.primary.main}}/>
                    ) : checkImages?.[checkSide] ? (
                        <Tooltip title="Click to view full size image" placement="right">
                            <img
                                onClick={() => setOpenImgModal(true)}
                                src={`data:image/png;base64,${checkImages[checkSide]}`}
                                alt=""
                                style={{objectFit: "contain", height: "100%", width: "90%", cursor: "pointer"}}
                            />
                        </Tooltip>
                    ) : (
                        <Typography variant="h5">
                            {filteredChecks?.length === 0
                                ? "No Deposit Data"
                                : "No Deposit Image"}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        maxHeight: "300px",
                        maxWidth: "800px",
                        padding: 0,
                        backgroundColor: "transparent",
                    }}
                >
                    {/* Previous Check Button */}
                    <IconButton
                        disabled={
                            currentCheckIndex <= 1 ||
                            filteredChecks?.length === 0 ||
                            !selectedCheck
                        }
                        onClick={() => handleCheckDirection(-1)}
                        sx={{
                            mx: 1,
                            p: 0,
                        }}
                    >
                        <ArrowBackIosNewIcon/>
                    </IconButton>
                    {/* Check Index */}
                    {selectedCheck && filteredChecks?.length !== 0 && (
                        <Typography
                            variant="subtitle1"
                            sx={{
                                textAlign: "center",
                                width: "100px",
                            }}
                        >
                            {`${currentCheckIndex} of ${filteredChecks.length}`}
                        </Typography>
                    )}
                    {/* Next Check Button */}
                    <IconButton
                        label="Select Next Check"
                        disabled={
                            currentCheckIndex === filteredChecks?.length ||
                            filteredChecks?.length === 0 ||
                            !selectedCheck
                        }
                        onClick={() => handleCheckDirection(1)}
                        sx={{
                            mx: 1,
                            p: 0,
                        }}
                    >
                        <ArrowForwardIosIcon/>
                    </IconButton>
                </Box>

                {/* Check Status Buttons */}
                <Box
                    sx={{
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                        visibility: selectedCheck ? "visible" : "hidden",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCheckSide}
                        sx={{
                            margin: 0.5,
                            width: "125px",
                            height: "40px",
                            lineHeight: "0px",
                        }}
                    >
                        {checkSide === "frontImage" ? "View Rear" : "View Front"}
                    </Button>

                    {/* Approved button */}
                    {!user.isViewOnlyRole && (
                        <LoadingButton
                            disabled={
                                selectedCheck?.reviewState !== "Pending"
                                || selectedCheck?.locked
                                || (selectedCheck?.validMicr === false && customerConfigs?.canApproveWithInvalidMicr === false)
                                || statusUpdateQueueRef.current.includes(selectedCheck?.checkID)
                                || !connectionId
                            }
                            variant="contained"
                            loading={loading.isLoading && loading.status === "Approved"}
                            onClick={() => handleStatusUpdate("Approved", "N/A")}
                            sx={{
                                backgroundColor: theme.palette.accent.success,
                                margin: 0.5,
                                width: "100px",
                                height: "40px",
                                lineHeight: "0px",
                            }}
                        >
                            Approve
                        </LoadingButton>
                    )}

                    {/* Rejected button */}
                    {!user.isViewOnlyRole && (
                        <LoadingButton
                            disabled={
                                selectedCheck?.reviewState !== "Pending"
                                || selectedCheck?.locked
                                || statusUpdateQueueRef.current.includes(selectedCheck?.checkID)
                                || !connectionId
                            }
                            variant="contained"
                            size="medium"
                            loading={loading.isLoading && loading.status === "Rejected"}
                            onClick={handleOpenRejectDialog}
                            sx={{
                                backgroundColor: theme.palette.accent.error,
                                margin: 0.5,
                                width: "100px",
                                height: "40px",
                                lineHeight: "0px",
                            }}
                        >
                            Reject
                        </LoadingButton>
                    )}
                </Box>

                {/* Selected Check Data Table */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                    }}
                >
                    <TableContainer
                        component={Paper}
                    >
                        <Table aria-label="simple table">
                            <TableHead sx={{backgroundColor: "primary.background"}}>
                                <TableRow>
                                    <TableCell sx={styles.tableHeader}>Item</TableCell>
                                    <TableCell sx={styles.tableHeader}>Deposit Time</TableCell>
                                    <TableCell sx={styles.tableHeader}>MICR</TableCell>
                                    <TableCell sx={styles.tableHeader}>Account</TableCell>
                                    <TableCell sx={styles.tableHeader}>Amount</TableCell>
                                    <TableCell sx={styles.tableHeader}>Status</TableCell>
                                    {selectedCheck?.reviewState !== "Pending" && (
                                        <TableCell sx={styles.tableHeader}>Reviewer</TableCell>)}
                                    {customerConfigs?.truechecksConfig?.enable && (<>
                                        <TableCell sx={styles.tableHeader}>TrueChecks® Action</TableCell>
                                        <TableCell sx={styles.tableHeader}>TrueChecks® Reason</TableCell>
                                    </>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow
                                    key={selectedCheck?.id}
                                    sx={{"&:last-child td, &:last-child th": {border: 0}}}
                                >
                                    <TableCell sx={styles.tableCell} component="th" scope="row">
                                        {selectedCheck?.checkID}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell}>
                                        {selectedCheck ? ReadableLocaleDate(selectedCheck?.date) : ""}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell}>
                                        {/* If there is a selectedCheck, render the TableCell */}
                                        {selectedCheck && showMicrField()}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell}>
                                        {selectedCheck?.accountNumber}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell}>
                                        {/* If there is a selectedCheck, render the TableCell */}
                                        {selectedCheck && showAmountField()}
                                    </TableCell>
                                    <TableCell sx={styles.tableCell}>
                                        {selectedCheck?.reviewState}
                                    </TableCell>
                                    {selectedCheck?.reviewState !== "Pending" && (
                                        <TableCell sx={styles.tableCell}>
                                            {selectedCheck?.primaryReviewer}
                                        </TableCell>
                                    )}
                                    {customerConfigs?.truechecksConfig?.enable && (
                                        <>
                                            <TableCell sx={styles.tableCell}>
                                                <TrueChecksActionCell action={selectedCheck?.truechecksRecommendedAction}/>
                                            </TableCell>
                                            <TableCell sx={styles.tableCell}>
                                                {selectedCheck?.truechecksRecommendedReason}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
            {openImgModal && (
                <DepositImgDialog
                    openImgModal={openImgModal}
                    setOpenImgModal={setOpenImgModal}
                    checkImages={checkImages}
                    checkSide={checkSide}
                />
            )}
            <RejectCheckDialog handleStatusUpdate={handleStatusUpdate} openRejectDialog={openRejectDialog} setOpenRejectDialog={setOpenRejectDialog}/>
        </>
    );
}
